import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { user } from 'src/StateObserver';
import PopupMatch2Base, {
  PopupMatch2BaseInitOptions,
} from '../../minigame/match2-timestep/components/PopupMatch2Base';
import { Match2Timer } from '../../minigame/match2-timestep/components/Match2Timer';
import {
  getStreakBattleRoyaleGoal,
  getStreakBattleRoyaleRewardGemsPool,
  getTimeToNextStreakBattleRoyale,
} from 'src/replicant/streakbattleroyale/streakbattleroyale.getters';
import { STREAK_EVENT_PLAYER_COUNT } from 'src/replicant/streakbattleroyale/streakbattleroyale.ruleset';
import gsap from 'gsap';
import {
  getStreakBattleRoyaleAssetPaths,
  streakBattleRoyaleTheme,
} from '../streakbattleroyale.theme';
import { AssetGroup } from '@play-co/timestep-core';
import StreakGlare from '../components/StreakGlare';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';

export default class PopupStreakBattleRoyaleIntroV2 extends PopupMatch2Base {
  private timeline: gsap.core.Timeline;
  private goal: number;
  private glare: StreakGlare;

  constructor(opts: { superview: View; close: (cta: boolean) => void }) {
    const width = 720;
    const height = 1080;
    const locale = StateObserver.getLocale();
    const isEn = locale === 'en';

    super({
      superview: opts.superview,
      width,
      height,
      panelImageOpts: {
        width,
        height,
        image: streakBattleRoyaleTheme().getAsset('hero'),
      },
      title: {
        type: 'image',
        opts: {
          scaleMethod: 'contain',
          canHandleEvents: false,
          image: streakBattleRoyaleTheme().getAsset(`title_event_${locale}`),
          width: isEn ? 408 : 576,
          height: isEn ? 104 : 105,
          scale: isEn ? 0.7 : 0.55,
          y: 90,
          zIndex: 10,
        },
      },
      cta: {
        localeText: () => i18n('match2.races.start'),
        onClick: async () => opts.close(true),
        positioning: 'inside',
        width: 347,
        offsetY: 10,
      },
      close: () => opts.close(false),
      tracking: {
        feature: Feature.MATCH2.STREAK_EVENT._,
        $subFeature: Feature.MATCH2.STREAK_EVENT.INTRO_POPUP,
      },
      zIndex: 10001,
    });

    this.goal = getStreakBattleRoyaleGoal(user());

    this.buttonClose!.updateOpts({
      x: width - 66,
      y: 112,
    });

    // hero image
    const hero = new ImageScaleView({
      superview: this.panel,
      image: streakBattleRoyaleTheme().getAsset('intro_cloud'),
      width: 340,
      height: 228,
      x: 133,
      y: 345,
      centerOnOrigin: true,
    });

    const mascot1 = new ImageScaleView({
      superview: this.panel,
      image: streakBattleRoyaleTheme().getAsset('intro_mascot1'),
      width: 138,
      height: 162,
      x: width - 142,
      y: height * 0.5 + 14,
      centerOnOrigin: true,
    });

    const mascot2 = new ImageScaleView({
      superview: this.panel,
      image: streakBattleRoyaleTheme().getAsset('intro_mascot2'),
      width: 222,
      height: 272,
      x: width * 0.5 + 40,
      y: height * 0.5 + 166,
      centerOnOrigin: true,
    });

    const mascot3 = new ImageScaleView({
      superview: this.panel,
      image: streakBattleRoyaleTheme().getAsset('intro_mascot3'),
      width: 225,
      height: 284,
      x: 136,
      y: height * 0.5 + 84,
      centerOnOrigin: true,
    });

    this.timeline = gsap
      .timeline()
      .to(
        hero.style,
        {
          duration: 4,
          offsetY: -140,
          ease: 'quad.inOut',
          repeat: -1,
          yoyo: true,
        },
        -1.5,
      )
      .to(mascot1.style, {
        duration: 2.5,
        offsetY: -100,
        r: -0.05,
        ease: 'quad.inOut',
        repeat: -1,
        yoyo: true,
      })
      .to(
        mascot2.style,
        {
          duration: 2,
          offsetY: -160,
          r: 0.05,
          ease: 'quad.inOut',
          repeat: -1,
          yoyo: true,
        },
        0.7,
      )
      .to(
        mascot3.style,
        {
          duration: 3,
          offsetY: -160,
          r: 0.1,
          ease: 'quad.inOut',
          repeat: -1,
          yoyo: true,
        },
        1.3,
      );

    // Platform
    new ImageScaleView({
      superview: this.panel,
      image: streakBattleRoyaleTheme().getAsset('platform'),
      width: 200,
      height: 81,
      x: width * 0.5 + 8,
      y: height * 0.5 - 40,
      centerOnOrigin: true,
    });

    // Glare VFX
    this.glare = new StreakGlare({
      superview: this.panel,
      opacity: 0.1,
      scale: 1.1,
      x: width * 0.5 + 4,
      y: height * 0.5 - 116,
    });

    // Chest
    new ImageScaleView({
      superview: this.panel,
      image: streakBattleRoyaleTheme().getAsset('chest'),
      scale: 0.9,
      width: 190,
      height: 214,
      x: width * 0.5 + 4,
      y: height * 0.5 - 114,
      centerOnOrigin: true,
    });

    // Speech bubble
    const prizePanel = new ImageScaleView({
      superview: this.panel,
      image: streakBattleRoyaleTheme().getAsset('panel_prize'),
      width: 244,
      height: 172,
      x: width - 190,
      y: 290,
      centerOnOrigin: true,
    });

    const baseOpts: LangBitmapOpts = {
      superview: prizePanel,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
    };

    // Prize label
    new LangBitmapFontTextShadowView({
      ...baseOpts,
      textShadowColor: '#8B1717',
      textShadowOpacity: 0.25,
      textShadowOffsetX: 0,
      textShadowOffsetY: 3,
      color: '#FFFFFF',
      font: 'Title',
      localeText: () => i18n('match2.streakBattleRoyale.popup.prizeV2'),
      size: 25,
      x: prizePanel.style.width * 0.5 - 2,
      y: 7,
    });

    // Prize value
    new LangBitmapFontTextView({
      ...baseOpts,
      color: '#266887',
      font: 'Body',
      localeText: () =>
        parseAmount(getStreakBattleRoyaleRewardGemsPool(user())),
      size: 34,
      x: prizePanel.style.width * 0.5 + 26,
      y: 54,
    });

    // event timer
    const timer = new Match2Timer({
      superview: this.panel,
      x: width * 0.5,
      y: height - 300,
      centerOnOrigin: true,
    });
    timer.setRemainingTimeGetter((now) =>
      getTimeToNextStreakBattleRoyale(user(), now),
    );
    timer.autoUpdate();

    // intro messages
    new LangBitmapFontTextView({
      superview: this.panel,
      size: 23,
      color: '#A06024',
      font: 'Body',
      localeText: () =>
        i18n('match2.streakBattleRoyale.intro.messageV2', {
          levels: this.goal,
          players: STREAK_EVENT_PLAYER_COUNT,
        }),
      x: width * 0.5,
      y: height - 216,
      width: width * 0.9,
      height: 130,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      wordWrap: true,
    });
  }

  public init(opts: PopupMatch2BaseInitOptions & { hideClose?: boolean }) {
    super.init(opts);
    if (opts.hideClose) {
      if (this.buttonCta)
        this.buttonCta.label.localeText = () => i18n('basic.okay');
      this.buttonClose?.removeFromSuperview();
    }
  }

  public override async fadeOut() {
    await super.fadeOut();
    this.timeline.kill();
    this.glare.stopAnimation();
  }

  protected getAssetsGroup() {
    const urls = getStreakBattleRoyaleAssetPaths();
    return new AssetGroup(urls);
  }
}
